// src/shared/components/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '@app/store/hooks';
import { logout } from '@app/store/slices/authSlice';
import { clearUser, setCompany } from '@app/store/slices/userSlice';
import { usePermissions } from '@core/hooks/usePermissions';
import { getPersianDate } from '@core/utils/dateUtils';
import { getRoleDisplayLabels } from '@core/constants/roleLabels';
import { refreshUserInfo } from '@shared/services/userService';
import { Modal } from '@shared/components';
import walletService, { WalletInfo } from '@features/wallet/services/walletService';
import { companyService } from '@features/companies/services/companyService';
import { API_BASE_URL } from '@core/config/runtimeConfig';
import { toast } from 'react-toastify';

const resolveCompanyLogoUrl = (value?: string | null): string | null => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const apiOrigin = API_BASE_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
  return `${apiOrigin}${value.startsWith('/') ? value : `/${value}`}`;
};

const ProfileRow: React.FC<{ label: string; value?: string | null; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) =>
  value ? (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <span className="text-gray-500">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-sm font-medium text-gray-900 break-words">{value}</div>
      </div>
    </div>
  ) : null;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, company } = useAppSelector((state) => state.user);
  const { online } = useAppSelector((state) => state.ui);
  const { roles, roleTitles, permissions } = usePermissions();
  const displayRoleLabels = getRoleDisplayLabels(roles, roleTitles);
  const isAdmin = roles.some((role) => role === 'admin');
  const isCompanyCEO = roles.some((role) => ['company_ceo', 'ceo', '89345904'].includes(String(role).toLowerCase()))
    || displayRoleLabels.some((label) => String(label).replace(/\s/g, '').includes('مدیرعامل'));
  const headerTheme = {
    gradient: 'bg-gradient-to-l from-blue-600 via-blue-700 to-indigo-700',
    dropdownGradient: 'bg-gradient-to-l from-blue-600 to-indigo-600',
    mutedText: 'text-blue-100',
  };
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const companyLogoUrl = resolveCompanyLogoUrl(company?.logo_url);
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);
  const companyInitial = String(company?.name || 'ش').trim().charAt(0) || 'ش';

  useEffect(() => {
    setCompanyLogoFailed(false);
  }, [companyLogoUrl]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearUser());
    navigate('/login_password');
  };

  const currentDate = getPersianDate(new Date());
  const rawName =
    [user?.name, user?.lastname].filter(Boolean).join(' ').trim() ||
    company?.name ||
    localStorage.getItem('name') ||
    'کاربر';
  // Strip trailing " - <role>" when it matches a known role so role is not duplicated (role is shown on the line below)
  const parts = rawName.split(' - ');
  const lastSegment = parts[parts.length - 1]?.trim() ?? '';
  const lastSegmentMatchesRole =
    displayRoleLabels.some((l) => l === lastSegment) ||
    displayRoleLabels.some((l) => l.replace(/\s/g, '') === lastSegment.replace(/\s/g, ''));
  const userName =
    parts.length > 1 && lastSegmentMatchesRole
      ? parts.slice(0, -1).join(' - ').trim() || rawName
      : rawName;
  const userMobile = user?.mobile_number || localStorage.getItem('mobile') || '';

  const handleCompanyLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setLogoUploading(true);
    try {
      const response = await companyService.uploadCompanyLogo(file);
      const updatedCompany = response?.company;
      const logoUrl = response?.logo_url ?? updatedCompany?.logo_url;
      if (company && logoUrl) {
        setCompanyLogoFailed(false);
        dispatch(setCompany({ ...company, ...updatedCompany, logo_url: logoUrl }));
      }
      await refreshUserInfo();
      toast.success('لوگوی شرکت با موفقیت ذخیره شد.');
    } catch (error: any) {
      toast.error(error?.message || 'ذخیره لوگوی شرکت ناموفق بود.');
    } finally {
      setLogoUploading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch wallet info
  useEffect(() => {
    walletService.getWallet()
      .then((res) => setWalletInfo(res.wallet))
      .catch(() => {});
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100, backgroundPosition: '0% 50%' }}
        animate={{ y: 0, backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={{
          y: { duration: 0.5, ease: 'easeOut' },
          backgroundPosition: { duration: 16, repeat: Infinity, ease: 'easeInOut' },
        }}
        className={`app-header sticky top-0 left-0 right-0 z-40 flex h-[var(--app-header-height,74px)] min-w-0 items-center justify-between gap-3 bg-[length:220%_220%] px-2 shadow-xl sm:px-5 2xl:px-8 ${headerTheme.gradient}`}
        dir="rtl"
      >
        {/* Company brand: logo on the right, then company name */}
        {company && (
          <div className="flex min-w-0 max-w-[75%] -translate-x-1 items-center gap-2.5 sm:max-w-none sm:translate-x-0 sm:gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/60 bg-white/20 text-base font-black text-white shadow-md sm:h-11 sm:w-11 sm:text-lg">
              {companyLogoUrl && !companyLogoFailed ? (
                <img
                  src={companyLogoUrl}
                  alt={`لوگوی ${company.name}`}
                  className="h-full w-full object-cover"
                  onError={() => setCompanyLogoFailed(true)}
                />
              ) : (
                <span aria-label={`نشان جایگزین ${company.name}`}>{companyInitial}</span>
              )}
            </div>
            <span className="min-w-0 max-w-[50vw] truncate text-sm font-bold leading-5 text-white sm:max-w-[240px] sm:text-base">
              {company.name}
            </span>
          </div>
        )}

        {/* Left-side utilities: date is intentionally the leftmost item */}
        <div dir="ltr" className="flex min-w-0 flex-1 items-center justify-start gap-1.5 md:flex-none md:gap-3 lg:gap-5">
          {/* Date Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            dir="rtl"
            className="hidden h-12 items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-3 backdrop-blur-sm sm:flex md:px-4"
          >
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium text-white">{currentDate}</span>
          </motion.div>

          {/* Online/Offline Status */}
          {!online && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              dir="rtl"
            className="flex h-10 items-center gap-2 rounded-xl border border-red-300/40 bg-red-500/20 px-3"
            >
              <div className="h-2 w-2 animate-pulse rounded-full bg-red-400" />
              <span className="text-xs font-medium text-white">آفلاین</span>
            </motion.div>
          )}

          {/* Wallet Balance - برای تمام کاربران */}
          {(
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/wallet')}
              className="flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-0 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 sm:h-12 sm:w-auto sm:justify-start sm:rounded-2xl sm:px-3 md:px-4"
              title="کیف پول"
              aria-label="کیف پول"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <div className="hidden text-right leading-tight sm:block">
                <div className={`text-[10px] ${headerTheme.mutedText}`}>کیف پول</div>
                <div className={`hidden text-sm font-black sm:block ${walletInfo && walletInfo.balance >= 0 ? 'text-emerald-200' : 'text-red-200'}`}>
                  {walletInfo ? `${new Intl.NumberFormat('fa-IR').format(walletInfo.balance)} ریال` : 'مشاهده موجودی'}
                </div>
              </div>
            </motion.button>
          )}

          {/* Admin Panel Link - فقط برای ادمین */}
          {isAdmin && (
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/admin/dashboard')}
              className="hidden h-12 items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-3 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 md:flex"
              title="رفتن به داشبورد مدیریت"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <span className="text-white font-medium text-sm">داشبورد مدیریت</span>
            </motion.button>
          )}

          {/* Profile Button with Dropdown */}
          <div className="relative order-first" ref={dropdownRef}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setShowProfileDropdown(!showProfileDropdown);
                setShowProfileModal(false);
              }}
              aria-label="حساب کاربری"
              aria-expanded={showProfileDropdown}
              aria-controls={showProfileDropdown ? 'header-profile-dropdown' : undefined}
              className="flex h-10 min-w-0 w-auto max-w-[calc(100vw-8rem)] shrink-0 items-center justify-start gap-2 rounded-xl border border-white/25 bg-white/10 px-2 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 sm:h-12 sm:max-w-none sm:justify-start sm:gap-3 sm:rounded-2xl sm:px-2.5"
            >
              {/* Avatar */}
              <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/50 bg-gradient-to-br from-white/30 to-white/10 sm:h-9 sm:w-9">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <svg
                className={`hidden h-4 w-4 text-white transition-transform duration-300 sm:block ${
                  showProfileDropdown ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <div dir="rtl" className="flex min-w-0 max-w-[18vw] flex-col text-right sm:max-w-none">
                <div className="max-w-full truncate text-[11px] font-black text-white sm:max-w-[150px] sm:text-sm">{userName}</div>
                {displayRoleLabels.length > 0 && (
                  <div className={`hidden max-w-[150px] truncate text-[10px] ${headerTheme.mutedText} opacity-90 sm:block`}>
                    {displayRoleLabels.join(', ')}
                  </div>
                )}
              </div>
            </motion.button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showProfileDropdown && (
                <motion.div
                  id="header-profile-dropdown"
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-full z-50 mt-2 max-h-[calc(100dvh-6rem)] w-64 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-2xl"
                  dir="rtl"
                >
                  <div className={`${headerTheme.dropdownGradient} px-4 py-4`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 shrink-0 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1 text-white">
                        <div className="break-words font-semibold text-sm">{userName}</div>
                        {userMobile && (
                          <div className={`text-xs ${headerTheme.mutedText} mt-0.5`}>{userMobile}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    <button
                      onClick={async () => {
                        setShowProfileDropdown(false);
                        setShowProfileModal(true);
                        try {
                          await refreshUserInfo();
                        } catch {
                          // keep showing modal with cached data
                        }
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-right text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium">مشاهده پروفایل</span>
                    </button>

                    {company && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        <div className="text-xs text-gray-500 mb-1">شرکت</div>
                        <div className="text-sm font-semibold text-gray-900">{company.name}</div>
                        {company.manager_name && (
                          <div className="text-xs text-gray-600 mt-0.5">مدیر: {company.manager_name}</div>
                        )}
                        {company.phone && (
                          <div className="text-xs text-gray-600 mt-0.5">{company.phone}</div>
                        )}
                      </div>
                    )}

                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-right text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        <span className="text-sm font-medium">خروج از حساب</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>

      {/* Profile Modal - full user and company profile */}
      <Modal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        title="اطلاعات کاربر"
        size="md"
      >
        <div className="space-y-6" dir="rtl">
          {/* User Avatar Section */}
          <div className="flex flex-col items-center pb-6 border-b border-gray-200">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">{userName}</h3>
            {userMobile && <p className="text-gray-600 mt-1">{userMobile}</p>}
          </div>

          {/* User profile - all fields from backend */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">اطلاعات شخصی</h4>
            <div className="space-y-2">
              <ProfileRow
                label="نام و نام خانوادگی"
                value={[user?.name, user?.lastname].filter(Boolean).join(' ').trim() || undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                }
              />
              <ProfileRow
                label="موبایل"
                value={user?.mobile_number ?? undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
              />
              <ProfileRow
                label="ایمیل"
                value={user?.email ?? undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              />
              <ProfileRow
                label="کد ملی"
                value={user?.national_id ?? undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                }
              />
              <ProfileRow
                label="تاریخ تولد"
                value={user?.birthdate ?? undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
              />
              <ProfileRow
                label="آدرس"
                value={user?.address ?? undefined}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
              />
            </div>
          </div>

          {/* Company profile - all fields from backend */}
          {company && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">اطلاعات شرکت</h4>
              <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-blue-200 bg-blue-100 text-xl font-black text-blue-700">
                  {companyLogoUrl && !companyLogoFailed ? (
                    <img
                      src={companyLogoUrl}
                      alt={`لوگوی ${company.name}`}
                      className="h-full w-full object-cover"
                      onError={() => setCompanyLogoFailed(true)}
                    />
                  ) : (
                    <span aria-label={`نشان جایگزین ${company.name}`}>{companyInitial}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500">لوگوی شرکت</div>
                  {isCompanyCEO ? (
                    <>
                      <input
                        id="company-logo-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleCompanyLogoUpload}
                        disabled={logoUploading}
                        className="sr-only"
                      />
                      <label
                        htmlFor="company-logo-upload"
                        className="mt-1 inline-flex cursor-pointer items-center rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
                      >
                        {logoUploading ? 'در حال بارگذاری...' : companyLogoUrl ? 'تغییر لوگو' : 'انتخاب لوگو'}
                      </label>
                      <p className="mt-1 text-[10px] text-gray-500">JPG، PNG یا WEBP تا ۲ مگابایت</p>
                    </>
                  ) : (
                    <p className="mt-1 text-xs text-gray-600">لوگوی شرکت توسط مدیرعامل مدیریت می‌شود.</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <ProfileRow
                  label="نام شرکت"
                  value={company.name}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  }
                />
                <ProfileRow label="مدیر" value={company.manager_name} icon={<span className="w-5 h-5 text-center text-lg">👤</span>} />
                <ProfileRow
                  label="تلفن شرکت"
                  value={company.phone ?? undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  }
                />
                <ProfileRow
                  label="ایمیل شرکت"
                  value={company.email ?? undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  }
                />
                <ProfileRow label="شناسه ملی شرکت" value={company.national_id ?? undefined} icon={<span className="w-5 h-5 text-center text-sm">#</span>} />
                <ProfileRow label="آدرس شرکت" value={company.address ?? undefined} icon={<span className="w-5 h-5 text-center">📍</span>} />
                <ProfileRow label="شعبه" value={company.branch_name ?? undefined} icon={<span className="w-5 h-5 text-center">🏢</span>} />
                <ProfileRow label="تلفن شعبه" value={company.branch_phone ?? undefined} icon={<span className="w-5 h-5 text-center">📞</span>} />
                <ProfileRow label="آدرس شعبه" value={company.branch_address ?? undefined} icon={<span className="w-5 h-5 text-center">📍</span>} />
                <ProfileRow label="دفتر" value={company.office_name ?? undefined} icon={<span className="w-5 h-5 text-center">🚪</span>} />
                <ProfileRow label="آدرس دفتر" value={company.office_address ?? undefined} icon={<span className="w-5 h-5 text-center">📍</span>} />
              </div>
            </div>
          )}

          {/* Roles & Permissions */}
          <div className="space-y-4">
            {displayRoleLabels.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-2">نقش‌ها</div>
                  <div className="flex flex-wrap gap-2">
                    {displayRoleLabels.map((label, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {permissions && permissions.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-2">تعداد دسترسی‌ها</div>
                  <div className="text-sm font-medium text-gray-900">{permissions.length} دسترسی</div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowProfileModal(false)}
              className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              بستن
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              خروج از حساب
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Header;

